import { Link } from 'react-router-dom';
import { Heart, Target, Shield, Users, ArrowRight, Lightbulb } from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: 'Humanidade primeiro',
    description: 'Finanças são pessoais. Cada pessoa tem uma história, um contexto, um ritmo. Não vendemos planilhas — oferecemos compreensão e direção.',
  },
  {
    icon: Target,
    title: 'Resultados reais',
    description: 'Não nos interessa parecer sofisticados. O que importa é que nossos usuários realizem seus sonhos e saiam das dívidas.',
  },
  {
    icon: Shield,
    title: 'Confiança radical',
    description: 'Seus dados nunca são vendidos. Nunca. Nossa receita vem de assinaturas, não de dados pessoais.',
  },
  {
    icon: Lightbulb,
    title: 'Educação contínua',
    description: 'Queremos que você precise cada vez menos de nós — mas que escolha ficar porque o valor é genuíno.',
  },
];

const team = [];

const milestones = [];

const About = () => {
  return (
    <div className="bg-white dark:bg-gray-950">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary-400 font-semibold text-sm uppercase tracking-widest mb-4">Nossa história</p>
          <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
            Nascemos da frustração.
            <br />
            <span className="text-primary-400">Crescemos da necessidade.</span>
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            Em 2021, dois amigos perceberam que os aplicativos financeiros existentes eram ou muito
            complicados ou muito superficiais. Nenhum deles era realmente um conselheiro. Eram
            apenas anotadores de gastos. Então decidiram construir o que queriam usar.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Nossa missão é democratizar a inteligência financeira.
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">
                Consultores financeiros de qualidade custam caro e são inacessíveis para a maioria da
                população brasileira. Acreditamos que orientação financeira personalizada deve ser um
                direito, não um privilégio.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                Usamos inteligência artificial para oferecer a cada usuário o mesmo nível de atenção
                e personalização que antes era reservado a clientes de private banking.
              </p>
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:gap-3 transition-all"
              >
                Conheça nossos recursos
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'IA', label: 'Conselheiro financeiro', color: 'bg-primary-50 dark:bg-primary-900/20 text-primary-600' },
                { value: '100%', label: 'Dados sob seu controle', color: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' },
                { value: 'Grátis', label: 'Para começar', color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600' },
                { value: 'Open', label: 'Finance integrado', color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600' },
              ].map((stat) => (
                <div key={stat.label} className={`rounded-2xl p-6 ${stat.color.split(' ').slice(0, -1).join(' ')}`}>
                  <p className={`text-3xl font-bold ${stat.color.split(' ').at(-1)}`}>{stat.value}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">O que nos guia</h2>
            <p className="text-xl text-gray-500 dark:text-gray-400">Nossos valores não estão em uma parede. Estão em cada decisão de produto.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="w-11 h-11 bg-primary-50 dark:bg-primary-900/30 rounded-xl flex items-center justify-center text-primary-600 mb-4">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{v.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      {milestones.length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-950">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Nossa trajetória</h2>
            </div>
            <div className="relative">
              <div className="absolute left-16 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-800" />
              <div className="space-y-8">
                {milestones.map((m) => (
                  <div key={m.year} className="flex gap-8 items-start relative">
                    <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0 z-10 shadow-lg">
                      {m.year.slice(2)}
                    </div>
                    <div className="pt-3">
                      <p className="text-xs text-primary-600 font-semibold mb-1">{m.year}</p>
                      <p className="text-gray-900 dark:text-white font-medium">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Team */}
      {team.length > 0 && (
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">O time por trás</h2>
              <p className="text-xl text-gray-500 dark:text-gray-400">Pessoas que escolheram dedicar seus talentos a um problema real e importante.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member) => (
                <div key={member.name} className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 text-center">
                  <div className={`w-16 h-16 rounded-full ${member.bg} flex items-center justify-center text-white font-bold text-lg mx-auto mb-4`}>
                    {member.avatar}
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{member.name}</h3>
                  <p className="text-primary-600 text-sm font-medium mb-3">{member.role}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Faça parte dessa história</h2>
          <p className="text-xl text-white/80 mb-8">Tome o controle das suas finanças com inteligência artificial.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors">
              Criar Conta Grátis
              <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/40 text-white rounded-xl font-semibold hover:border-white transition-colors">
              Falar Conosco
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
